import React, { useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Paper,
    Tabs,
    Tab,
    LinearProgress,
    Avatar,
    Chip,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
} from "@mui/material";
import {
    BookOpen,
    FileText,
    HelpCircle,
    MessageSquare,
    Users,
    Calendar,
    ChevronRight,
    MoreVertical,
    PlayCircle,
    Download,
    CheckCircle2,
    Lock,
    ArrowLeft,
    Settings,
    Edit2,
    Info,
    ExternalLink,
    Plus,
    X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import backgroundImage from "../../assets/Vector 17.svg";

const CourseDetail = () => {
    const [tabValue, setTabValue] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();

    const courseInfo = {
        title: "Advanced Algebra & Calculus",
        tutor: "Dr. John Smith",
        progress: 75,
        description: "Master the complexities of modern calculus and algebraic structures. This course covers everything from limits to differential equations.",
        category: "Mathematics & Logic",
        startDate: "2024-01-15",
        endDate: "2024-05-30",
        totalSubjects: 3,
        totalMembers: 1205
    };

    const subjects = [
        {
            id: "1",
            name: "Mathematics",
            topics: [
                { name: "Complex Numbers", duration: "2 weeks", subtopics: ["Imaginary Unit", "Conjugates"] },
                { name: "Roots of Unity", duration: "1 week", subtopics: ["De Moivre's Theorem"] }
            ],
            startDate: "2024-01-15",
            endDate: "2024-02-15",
            references: {
                studyNotes: "Calculus Fundamentals",
                booklet: "Module 1 Booklet",
                videoTutorials: "Concept Summary"
            }
        },
        // ... more can be added
    ];

    const courseTopics = [
        {
            id: 1,
            name: "Complex Numbers & Roots",
            completed: true,
            activities: [
                { type: "pdf", title: "Intro to Complex Planes", duration: "1.2 MB", status: "completed" },
                { type: "quiz", title: "Practice Quiz: De Moivre's Theorem", duration: "15 questions", status: "completed" },
                { type: "video", title: "Polar Form Deep Dive", duration: "18:45", status: "completed" },
            ]
        },
        {
            id: 2,
            name: "Calculus: Differentiation",
            completed: true,
            activities: [
                { type: "video", title: "Chain Rule Mastery", duration: "25:10", status: "completed" },
                { type: "assignment", title: "Weekly Problem Set - Level 2", duration: "Due Jan 28", status: "submitted" },
                { type: "pdf", title: "Differentiation Cheat Sheet", duration: "450 KB", status: "completed" },
            ]
        },
        {
            id: 3,
            name: "Calculus: Integration Techniques",
            completed: false,
            activities: [
                { type: "video", title: "Integration by Parts", duration: "22:00", status: "in-progress" },
                { type: "assignment", title: "Integration Challenge", duration: "Due Feb 04", status: "pending" },
                { type: "quiz", title: "Advanced Integration Quiz", duration: "10 questions", status: "locked" },
            ]
        },
    ];

    const getActivityIcon = (type: string) => {
        switch (type) {
            case "video": return <PlayCircle size={20} color="#4f46e5" />;
            case "assignment": return <FileText size={20} color="#06b6d4" />;
            case "quiz": return <HelpCircle size={20} color="#f59e0b" />;
            case "pdf": return <Download size={20} color="#10b981" />;
            default: return <BookOpen size={20} />;
        }
    };

    return (
        <Box sx={{  bgcolor: "#f8fafc", minHeight: "100vh" }}>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                {/* Back and Actions */}
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                    <Button
                        startIcon={<ArrowLeft size={18} />}
                        onClick={() => navigate("/online-tutoring/courses")}
                        sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }}
                    >
                        Back to Catalog
                    </Button>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                            onClick={() => {
                                setTabValue(1);
                                setIsEditing(!isEditing);
                            }}
                            sx={{ 
                                bgcolor: isEditing ? "#10b981" : "#fff", 
                                color: isEditing ? "#fff" : "#4f46e5", 
                                border: "1px solid #e2e8f0",
                                borderRadius: "10px",
                                "&:hover": { bgcolor: isEditing ? "#059669" : "#f1f5f9" }
                            }}
                        >
                            {isEditing ? <CheckCircle2 size={20} /> : <Edit2 size={20} />}
                        </IconButton>
                        <IconButton sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px" }}><Users size={18} /></IconButton>
                        <IconButton sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px" }}><Settings size={18} /></IconButton>
                    </Box>
                </Box>

                {/* Header Hero Area */}
                <Paper 
                    elevation={0}
                    sx={{
                        p: 3,
                        borderRadius: "32px",
                        position: "relative",
                        overflow: "hidden",
                        mb: 4,
                        background: "linear-gradient(90deg, #009DFF, #017CC9)",
                        color: "#fff",
                        boxShadow: "0 20px 40px rgba(30, 64, 175, 0.15)",
                    }}
                >
                    {/* Background Vector Shape */}
                    <Box
                        sx={{
                            position: "absolute",
                            right: -50,
                            bottom: -20,
                            width: "50%",
                            height: "100%",
                            opacity: 0.15,
                            zIndex: 0,
                            pointerEvents: "none",
                            "& img": {
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                transform: "rotate(-5deg) scale(1.2)"
                            }
                        }}
                    >
                        <img src={backgroundImage} alt="" />
                    </Box>

                    <Box sx={{ position: "relative", zIndex: 1 }}>
                        <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Chip
                                label={courseInfo.category}
                                sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "#f9f9faff", fontWeight: 700, mb: 2, border: "1px solid rgba(255,255,255,0.1)" }}
                            />
                            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
                                {courseInfo.title}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Avatar src="/tutor.jpg" sx={{ width: 32, height: 32 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{courseInfo.tutor}</Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Users size={18} color="#ffffffff" />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>1,205 Students Enrolled</Typography>
                                </Box>
                            </Box>
                            <Typography variant="body1" sx={{ color: "#ffffffff", mb: 4, lineHeight: 1.7, maxWidth: "600px" }}>
                                {courseInfo.description}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{
                                p: 4,
                                background: "rgba(255,255,255,0.03)",
                                borderRadius: "28px",
                                backdropFilter: "blur(12px)",
                                border: "1px solid rgba(255,255,255,0.1)",
                                position: "relative",
                                zIndex: 1,
                                boxShadow: "inset 0 0 30px rgba(255,255,255,0.05)"
                            }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: "#94a3b8" }}>
                                    Course Progress
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5 }}>
                                    {courseInfo.progress}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={courseInfo.progress}
                                    sx={{
                                        height: 10,
                                        borderRadius: 5,
                                        bgcolor: "rgba(255,255,255,0.1)",
                                        "& .MuiLinearProgress-bar": { bgcolor: "#4f46e5", borderRadius: 5 }
                                    }}
                                />
                                <Button
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        mt: 3,
                                        borderRadius: "14px",
                                        py: 1.5,
                                        background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                                        textTransform: "none",
                                        fontWeight: 700,
                                        color: "#fff",
                                        "&:hover": { background: "linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)" },
                                        boxShadow: "0 4px 12px rgba(79, 70, 229, 0.3)"
                                    }}
                                >
                                    Join Live Session
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

                {/* Course Content Tabs */}
                <Box sx={{ borderBottom: "1px solid #e2e8f0", mb: 4 }}>
                    <Tabs
                        value={tabValue}
                        onChange={(e, v) => {
                            setTabValue(v);
                            if (v !== 1) setIsEditing(false);
                        }}
                        sx={{
                            minHeight: "34px",
                            height: "34px",
                            "& .MuiTab-root": { 
                                textTransform: "none", 
                                fontWeight: 700, 
                                fontSize: "14px", 
                                color: "#64748b", 
                                minHeight: "40px",
                                height: "40px",
                                padding: "4px 16px",
                                borderRadius: "8px"
                            },
                            "& .Mui-selected": { color: "#4f46e5 !important" },
                            "& .MuiTabs-indicator": { bgcolor: "#4f46e5", height: 2, borderRadius: "2px 2px 0 0"}
                        }}
                    >
                        <Tab label="Curriculum" icon={<BookOpen size={18} />} iconPosition="start" />
                        <Tab label="Course Details" icon={<Info size={18} />} iconPosition="start" />
                        <Tab label="Resources" icon={<Download size={18} />} iconPosition="start" />
                        <Tab label="Participants" icon={<Users size={18} />} iconPosition="start" />
                    </Tabs>
                </Box>

                <Grid container spacing={4}>
                    <Grid item xs={12} lg={8}>
                        {tabValue === 0 && (
                            <Box>
                                {courseTopics.map((topic, idx) => (
                                    <Paper key={topic.id} sx={{
                                        mb: 3,
                                        borderRadius: "24px",
                                        overflow: "hidden",
                                        border: "1px solid #f1f5f9",
                                        boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                                    }}>
                                        <Box sx={{
                                            p: 2.5,
                                            bgcolor: "#f8fafc",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            borderBottom: "1px solid #f1f5f9"
                                        }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                <Box sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: "10px",
                                                    bgcolor: topic.completed ? "#10b981" : "#e2e8f0",
                                                    color: topic.completed ? "#fff" : "#64748b",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontWeight: 800
                                                }}>
                                                    {topic.completed ? <CheckCircle2 size={24} /> : topic.id}
                                                </Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", fontSize: "1.1rem" }}>
                                                    {topic.name}
                                                </Typography>
                                            </Box>
                                            <IconButton><ChevronRight size={20} /></IconButton>
                                        </Box>
                                        <List disablePadding>
                                            {topic.activities.map((activity, aIdx) => (
                                                <ListItem
                                                    key={aIdx}
                                                    onClick={() => {
                                                        if (activity.status !== "locked") {
                                                            if (activity.type === "assignment") navigate("/online-tutoring/assignments/view");
                                                            if (activity.type === "quiz") navigate("/online-tutoring/quizzes/view");
                                                        }
                                                    }}
                                                    sx={{
                                                        px: 3,
                                                        py: 2,
                                                        borderBottom: aIdx < topic.activities.length - 1 ? "1px solid #f1f5f9" : "none",
                                                        opacity: activity.status === "locked" ? 0.6 : 1,
                                                        "&:hover": { bgcolor: activity.status === "locked" ? "transparent" : "#f1f5f9", cursor: activity.status === "locked" ? "default" : "pointer" }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 48 }}>
                                                        {getActivityIcon(activity.type)}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={activity.title}
                                                        secondary={activity.duration}
                                                        primaryTypographyProps={{ sx: { fontWeight: 600, color: "#1e293b", fontSize: "0.95rem" } }}
                                                        secondaryTypographyProps={{ sx: { color: "#64748b", fontWeight: 500 } }}
                                                    />
                                                    <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
                                                        {activity.status === "completed" && (
                                                            <Chip size="small" label="Finished" sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 700, borderRadius: "6px" }} />
                                                        )}
                                                        {activity.status === "submitted" && (
                                                            <Chip size="small" label="Grading" sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 700, borderRadius: "6px" }} />
                                                        )}
                                                        {activity.status === "locked" ? (
                                                            <Lock size={18} color="#94a3b8" />
                                                        ) : (
                                                            <ChevronRight size={18} color="#94a3b8" />
                                                        )}
                                                    </Box>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                ))}
                            </Box>
                        )}

                        {tabValue === 1 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {/* Basic Information Accordion */}
                                <Accordion defaultExpanded sx={{ borderRadius: "24px !important", border: "1px solid #f1f5f9", boxShadow: "none", "&:before": { display: "none" } }}>
                                    <AccordionSummary expandIcon={<ChevronRight size={18} />} sx={{ px: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Info size={20} color="#4f46e5" /> Basic Information
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 3, pb: 4 }}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Course Title</Typography>
                                                {isEditing ? (
                                                    <TextField fullWidth size="small" defaultValue={courseInfo.title} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                                                ) : (
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{courseInfo.title}</Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Category</Typography>
                                                {isEditing ? (
                                                    <FormControl fullWidth size="small">
                                                        <Select defaultValue={courseInfo.category} sx={{ borderRadius: "12px" }}>
                                                            <MenuItem value={courseInfo.category}>{courseInfo.category}</MenuItem>
                                                            <MenuItem value="Science">Science</MenuItem>
                                                            <MenuItem value="History">History</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                ) : (
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{courseInfo.category}</Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Description</Typography>
                                                {isEditing ? (
                                                    <TextField fullWidth multiline rows={3} defaultValue={courseInfo.description} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                                                ) : (
                                                    <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>{courseInfo.description}</Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Schedule Accordion */}
                                <Accordion defaultExpanded sx={{ borderRadius: "24px !important", border: "1px solid #f1f5f9", boxShadow: "none", "&:before": { display: "none" } }}>
                                    <AccordionSummary expandIcon={<ChevronRight size={18} />} sx={{ px: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Calendar size={20} color="#4f46e5" /> Course Schedule
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 3, pb: 4 }}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Start Date</Typography>
                                                {isEditing ? (
                                                    <TextField fullWidth type="date" defaultValue={courseInfo.startDate} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                                                ) : (
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{courseInfo.startDate}</Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>End Date</Typography>
                                                {isEditing ? (
                                                    <TextField fullWidth type="date" defaultValue={courseInfo.endDate} size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                                                ) : (
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{courseInfo.endDate}</Typography>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Subjects & Curriculum Accordion */}
                                <Accordion defaultExpanded sx={{ borderRadius: "24px !important", border: "1px solid #f1f5f9", boxShadow: "none", "&:before": { display: "none" } }}>
                                    <AccordionSummary expandIcon={<ChevronRight size={18} />} sx={{ px: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1e293b", display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <BookOpen size={20} color="#4f46e5" /> Subjects & Curriculum
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ px: 3, pb: 4 }}>
                                        {subjects.map((subject, sIdx) => (
                                            <Card key={subject.id} variant="outlined" sx={{ mb: 3, borderRadius: "20px", border: "1px solid #f1f5f9", overflow: "hidden", boxShadow: "0 4px 15px rgba(0,0,0,0.02)" }}>
                                                <Box sx={{ 
                                                    px: 3, 
                                                    py: 2, 
                                                    background: "linear-gradient(90deg, #f8fafc 0%, #eff6ff 100%)", 
                                                    borderBottom: "1px solid #f1f5f9", 
                                                    display: "flex", 
                                                    justifyContent: "space-between", 
                                                    alignItems: "center" 
                                                }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#4f46e5" }}>{isEditing ? `Subject ${sIdx + 1}` : subject.name}</Typography>
                                                    {!isEditing && (
                                                        <Chip 
                                                            label={`${subject.startDate} - ${subject.endDate}`} 
                                                            size="small" 
                                                            sx={{ 
                                                                fontWeight: 700, 
                                                                bgcolor: "rgba(255,255,255,0.8)", 
                                                                backdropFilter: "blur(4px)",
                                                                border: "1px solid #e2e8f0" 
                                                            }} 
                                                        />
                                                    )}
                                                </Box>
                                                <Box sx={{ p: 3 }}>
                                                    <Grid container spacing={3}>
                                                        {isEditing && (
                                                            <Grid item xs={12} md={6}>
                                                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Subject Name</Typography>
                                                                <TextField fullWidth size="small" defaultValue={subject.name} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                                                            </Grid>
                                                        )}
                                                        <Grid item xs={12} md={isEditing ? 6 : 12}>
                                                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, mb: 1, display: "block" }}>Topics</Typography>
                                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                                {subject.topics.map((t, tIdx) => (
                                                                    <Chip 
                                                                        key={tIdx} 
                                                                        label={t.name} 
                                                                        onDelete={isEditing ? () => {} : undefined}
                                                                        sx={{ fontWeight: 600, borderRadius: "8px", bgcolor: isEditing ? "#eef2ff" : "#f1f5f9" }} 
                                                                    />
                                                                ))}
                                                                {isEditing && (
                                                                    <Button size="small" startIcon={<Plus size={14} />} sx={{ textTransform: "none", fontWeight: 700 }}>Add Topic</Button>
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                                                                Reference Materials
                                                            </Typography>
                                                            <Grid container spacing={2}>
                                                                {[
                                                                    { label: "Study Notes", value: subject.references.studyNotes },
                                                                    { label: "Booklet", value: subject.references.booklet },
                                                                    { label: "Videos", value: subject.references.videoTutorials }
                                                                ].map((ref, rIdx) => (
                                                                    <Grid item xs={12} md={4} key={rIdx}>
                                                                        <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "12px", border: "1px solid #f1f5f9" }}>
                                                                            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, display: "block", mb: 0.5 }}>{ref.label}</Typography>
                                                                            {isEditing ? (
                                                                                <select style={{ width: "100%", padding: "4px", border: "none", background: "transparent", fontWeight: 700 }}>
                                                                                    <option>{ref.value}</option>
                                                                                </select>
                                                                            ) : (
                                                                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#1e293b" }}>{ref.value}</Typography>
                                                                            )}
                                                                        </Box>
                                                                    </Grid>
                                                                ))}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Card>
                                        ))}
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        )}
                    </Grid>

                    {/* Right Sidebar - Moodle Widgets */}
                    <Grid item xs={12} lg={4}>
                        <Paper sx={{ p: 4, borderRadius: "24px", bgcolor: "#fff", border: "1px solid #f1f5f9", mb: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Calendar size={22} color="#4f46e5" />
                                Deadlines
                            </Typography>
                            <List disablePadding>
                                {[
                                    { title: "Algebra Assignment", due: "Due Tomorrow, 11:59 PM", type: "urgent" },
                                    { title: "Integration Quiz", due: "Opens Feb 04", type: "future" },
                                ].map((item, idx) => (
                                    <Box key={idx} sx={{
                                        p: 2.5,
                                        borderRadius: "18px",
                                        bgcolor: item.type === "urgent" ? "#fff1f2" : "#f8fafc",
                                        mb: 2,
                                        border: item.type === "urgent" ? "1px solid #fecaca" : "1px solid transparent"
                                    }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: item.type === "urgent" ? "#991b1b" : "#1e293b", mb: 0.5 }}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: item.type === "urgent" ? "#ef4444" : "#64748b", fontWeight: 600 }}>
                                            {item.due}
                                        </Typography>
                                    </Box>
                                ))}
                            </List>
                            <Button fullWidth variant="contained" sx={{ mt: 2, borderRadius: "12px", bgcolor: "#f1f5f9", color: "#1e293b", boxShadow: "none", fontWeight: 700, "&:hover": { bgcolor: "#e2e8f0" } }}>
                                Full Schedule
                            </Button>
                        </Paper>

                        <Paper sx={{ p: 4, borderRadius: "24px", bgcolor: "#4f46e5", color: "#fff", position: "relative", overflow: "hidden" }}>
                            <Box sx={{ position: "relative", zIndex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Need Help?</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mb: 3 }}>Message your tutor or start a discussion with classmates.</Typography>
                                <Button variant="contained" sx={{ bgcolor: "#fff", color: "#4f46e5", fontWeight: 700, textTransform: "none", borderRadius: "10px", "&:hover": { bgcolor: "#f8fafc" } }}>
                                    Contact Tutor
                                </Button>
                            </Box>
                            <MessageSquare size={120} style={{ position: "absolute", bottom: -20, right: -20, opacity: 0.1 }} />
                        </Paper>
                    </Grid>
                </Grid>
            </motion.div>
        </Box>
    );
};

export default CourseDetail;
